import { type MaybeElementRef } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FilterComparator,
  FilterOperator,
  SortDirection,
  type GroupingFilterExpression,
} from '@@/packages/shared/libs/search/types';
import {
  type Asset,
  type AssetScanResultWithMetadata,
  ScanTypeName,
} from '@@/packages/shared/types/models/monitoring';
import useSearchQueryParams, {
  type SearchQueryParams,
} from '@@/packages/shared/libs/search/useQueryParams';
import type { AssetScanResultWithMetadataAndAsset } from './export';

export function useScanTypeCategories() {
  const { t } = useI18n();

  const scanTypeByCategories = {
    portSecurity: [
      ScanTypeName.RemoteAccessPorts,
      ScanTypeName.DatabasePorts,
      ScanTypeName.WebServicePorts,
      ScanTypeName.FileSharingPorts,
      ScanTypeName.EmailPorts,
      ScanTypeName.InfrastructurePorts,
    ],
    emailSecurity: [ScanTypeName.SPF, ScanTypeName.DKIM, ScanTypeName.DMARC],
    webSecurity: [
      ScanTypeName.HSTS,
      ScanTypeName.CSP,
      ScanTypeName.SecurityHeaders,
      ScanTypeName.TLS,
    ],
    dnsSecurity: [ScanTypeName.DNS],
  };

  function getCategoryForScanType(scanType: ScanTypeName): string {
    for (const [categoryKey, scanTypes] of Object.entries(scanTypeByCategories)) {
      if (scanTypes.includes(scanType)) {
        return t(`externalMonitoring.categories.${categoryKey}`);
      }
    }

    // Fallback to scan type name if no category found
    return scanType;
  }

  /**
   * Get the raw category key for a given scan type (without translation)
   */
  function getCategoryKeyForScanType(scanType: ScanTypeName): string | null {
    for (const [categoryKey, scanTypes] of Object.entries(scanTypeByCategories)) {
      if (scanTypes.includes(scanType)) {
        return categoryKey;
      }
    }

    return null;
  }

  return {
    scanTypeByCategories,
    getCategoryForScanType,
    getCategoryKeyForScanType,
  };
}

// Adds the asset to each scan result
export function useScanResultsWithAsset(
  assets: MaybeRefOrGetter<Asset[]>,
  scanResults: MaybeRefOrGetter<AssetScanResultWithMetadata[]>,
): ComputedRef<AssetScanResultWithMetadataAndAsset[]> {
  return computed(() =>
    toValue(scanResults).map((result) => {
      const asset = toValue(assets).find((a) => a.id === result.assetID);
      return { scanResult: result, asset };
    }),
  );
}

// When mounted, if the element is the first element matching the selector, it returns true for expanded.
export function useExpandedIfFirstElementOfSelector(
  el: MaybeElementRef,
  selector: MaybeRefOrGetter<string>,
) {
  const isExpanded = ref(false);
  onMounted(() => {
    const selection = document.querySelector(`${toValue(selector)}`);
    if (selection === toValue(el)) {
      isExpanded.value = true;
    }
  });

  return isExpanded;
}

function searchQueryParamsLatestScanResultFindings(
  filter: GroupingFilterExpression | undefined,
): SearchQueryParams {
  return {
    filter,
    sort: [{ field: 'scanned_at', direction: SortDirection.Descending }],
    page: { limit: 25, offset: 0 },
  };
}

export function searchQueryParamsLatestScanResultFindingsForAsset(assetName?: string) {
  const filter = assetName
    ? {
        operator: FilterOperator.And,
        operands: [
          { field: 'asset_value', comparator: FilterComparator.IsAny, value: [assetName] },
        ],
      }
    : undefined;
  return searchQueryParamsLatestScanResultFindings(filter);
}

export function useSearchQueryParamsLatestScanResultFindings() {
  return useSearchQueryParams(searchQueryParamsLatestScanResultFindings(undefined));
}

export function useIsPortScanType(scanType: MaybeRefOrGetter<string>) {
  return computed(() => toValue(scanType).endsWith('Ports'));
}
